import {useContext, useState, MutableRefObject, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {CategorizedApplication} from '../../../../../../../backend/responses/categorized-application';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {PageHeader} from '../../../../../../../components/page-header/page-header';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../../../../../enums/screen-path';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {ApplicationsRepository} from '../../../../../../../repositories/applications';
import {ApplicationsStateModal} from '../../../../../../../components/user-applications/modals/applications-state-modal';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Category = 'category',
  State = 'state',
}

export default function EntityApplicationsSystemAdministratorOfficeScreen() {
  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold state
  const [updateApplicationDialogOpened, setUpdateApplicationDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<
    {applicationId: string; applicationName: string; applicationActiveFlag: boolean} | undefined
  > = useRef();

  // Get params
  let {id}: {id?: string} = useParams();

  // Translations
  const {t} = useTranslation();

  // Fetch applications
  const handleGetApplications = async (limit: number, skip: number, search?: string) => {
    // Loads applications
    const result: {totalCount: number; data: Array<CategorizedApplication>} =
      await ApplicationsRepository.instance.getInEntityAsSystemAdministrator(
        id || '',
        skip,
        limit,
        search
      );

    // Process data
    const processedData: PaginatedTableData<CategorizedApplication> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.application.id || '',
        item: obj,
        avatarUrl: obj.application.picture,
        enabled: obj.application.isActive,
        toLink: `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Applications}/${obj.application.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            // The name is localized
            value: obj.application.name || '',
          },
          {
            columnId: ColumnIdentifier.Category,
            // The name is localized
            value: obj.category.name || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.application.isActive
              ? t(translations.entities.edit.applications.state_value_true)
              : t(translations.entities.edit.applications.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<CategorizedApplication>({
    fetchData: handleGetApplications,
  });

  const handleAskUpdateApplicationState = (applicationId: string, value: boolean) => {
    // Get application name
    const applicationName: string | undefined = data
      .find((obj) => obj.id === applicationId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set application info for later
    dataToUpdateRef.current = {
      applicationId: applicationId,
      applicationName: applicationName || '',
      applicationActiveFlag: value,
    };

    // Open modal
    setUpdateApplicationDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdateApplicationState = () => {
    // Close modal
    setUpdateApplicationDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdateApplicationState = () => {
    // Close modal
    setUpdateApplicationDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdateApplicationState();
  };

  const handleUpdateApplicationState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const applicationId = dataToUpdateRef.current.applicationId;
      const value = dataToUpdateRef.current.applicationActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === applicationId)?.item;
      if (updatedItem) {
        updatedItem.application.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        applicationId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.entities.edit.applications.state_value_true)
              : t(translations.entities.edit.applications.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdateApplicationState(id || '', applicationId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.entities.edit.applications.errors.update_state_application_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the application. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdateApplicationState = async (
    entityId: string,
    applicationId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await ApplicationsRepository.instance.updateActiveStateForEntityAsSystemAdministrator(
        applicationId,
        entityId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === applicationId)?.item;
      if (updatedItem) {
        updatedItem.application.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        applicationId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.entities.edit.applications.state_value_true)
              : t(translations.entities.edit.applications.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(translations.entities.edit.applications.errors.enable_application_error_message)
          : t(translations.entities.edit.applications.errors.disable_application_error_message),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the application with id ${applicationId} to ${value} for entity with id ${entityId}.`,
        error
      );
    }
  };

  return (
    <ContentPage>
      <PageHeader
        showSearch={true}
        searchLabel={t(translations.entities.edit.applications.search_input_placeholder)}
        searchText={currentSearch}
        searchOnChange={(text: string) => {
          // Update text search
          handleUpdateCurrentSearch(text);
        }}
        searchOnSubmit={() => {
          // Query backend about this
          handleApplyFilters(1, currentSearch);
        }}
        disableSearch={isLoading}
      />
      <PaginatedTable
        avatar={{
          type: 'other',
        }}
        emptyTableMessage={t(translations.entities.edit.applications.no_applications)}
        columns={[
          {
            columnId: ColumnIdentifier.Name,
            title: t(translations.entities.edit.applications.name_header),
            flexGrow: 1,
          },
          {
            columnId: ColumnIdentifier.Category,
            title: t(translations.entities.edit.applications.category_header),
            flexGrow: 1,
          },
          {
            columnId: ColumnIdentifier.State,
            title: t(translations.entities.edit.applications.state_header),
            flexGrow: 1,
            button: {
              type: 'toggle',
              showColumnValue: true,
              toggle: {
                enabledValue: t(translations.entities.edit.applications.state_value_true),
                handleClick: handleAskUpdateApplicationState,
              },
            },
          },
        ]}
        rows={data}
        isLoading={isLoading}
        error={error}
        pagesCount={pagesCount}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        linkableRows={true}
      />
      <ApplicationsStateModal
        open={
          updateApplicationDialogOpened.disableDialogOpened ||
          updateApplicationDialogOpened.enableDialogOpened
        }
        state={updateApplicationDialogOpened.disableDialogOpened ? 'disable' : 'enable'}
        applicationName={dataToUpdateRef.current?.applicationName || ''}
        description={t(
          translations.entities.edit.applications[
            `${updateApplicationDialogOpened.disableDialogOpened ? 'disable' : 'enable'}_application_dialog_message`
          ],
          {
            name: officeContext.state.entityForm?.name || '',
          }
        )}
        onClose={handleDeclineUpdateApplicationState}
        onConfirm={handleConfirmUpdateApplicationState}
      />
    </ContentPage>
  );
}
